import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Button, CircularProgress, Snackbar, Alert,
  Modal // Added Modal import
} from '@mui/material';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';

// Animation variants for modal content
const modalVariants = {
  hidden: { opacity: 0, scale: 0.4 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut" }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.3, ease: "easeIn" }
  }
};

// Animation variants for form fields
const fieldVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: "easeOut" }
  }
};

// Animation variants for buttons
const buttonVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut" }
  },
  hover: {
    scale: 1.05,
    transition: { duration: 0.2 }
  }
};

const SliderEdit = ({ open, handleClose, slider, onSliderUpdated }) => {
  // State for form fields
  const [sliderLink, setSliderLink] = useState('');
  const [sliderImg, setSliderImg] = useState('');
  const [clientName, setClientName] = useState('');
  const [trending, setTrending] = useState(false);
  const [status, setStatus] = useState('live');
  const [brands, setBrands] = useState([]);

  // State for UI feedback
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Populate form fields when slider prop changes
  useEffect(() => {
    if (slider) {
      setSliderLink(slider.link || '');
      setSliderImg(slider.image || '');
      setClientName(slider.clientName || '');
      setTrending(slider.trending === 'Yes');
      setStatus(slider.status || 'live');
    }
  }, [slider]);

  // Fetch brand list from API when component mounts
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/brands`);
        const data = await response.json();
        if (data && data.brands) {
          setBrands(data.brands);
        } else {
          setError('Unexpected response format for brands.');
          setSnackbarMessage('Unexpected response format for brands.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        setError('Failed to fetch brands. Please try again.');
        setSnackbarMessage('Failed to fetch brands. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const data = {
      slider_link: sliderLink,
      slider_img: sliderImg,
      client_name: clientName,
      Trending: trending,
      status: status,
    };

    try {
      const response = await fetch(`${BASE_URL}/sliders/${slider?.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      if (response.ok) {
        onSliderUpdated();
        setSnackbarMessage('Slider updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
      } else {
        const errorMessage = result.message || 'Failed to update slider. Please check your inputs.';
        setError(errorMessage);
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error updating slider:', error);
      const errorMessage = 'Error updating slider. Please try again.';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-slider-modal-title"
      aria-describedby="edit-slider-modal-description"
    >
      <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 650,
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography
            id="edit-slider-modal-title"
            variant="h6"
            component="h2"
            sx={{ color: '#2e7d32', fontWeight: 'bold', }}
          >
            Edit Slider
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
            }}
          >
            {sliderImg && (
              <motion.div variants={fieldVariants}>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    // padding: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    // minHeight: '100px',
                  }}
                >
                  <img
                    src={sliderImg}
                    alt="Slider Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      height: 'auto',
                      borderRadius: '4px',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/200x150/EEEEEE/333333?text=Image+Load+Error';
                    }}
                  />
                </Box>
              </motion.div>
            )}

            {error && (
              <motion.div variants={fieldVariants}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </motion.div>
            )}

            <motion.div variants={fieldVariants}>
              <TextField
                label="Slider Link"
                id="sliderLink"
                value={sliderLink}
                onChange={(e) => setSliderLink(e.target.value)}
                required
                fullWidth
                variant="outlined"
                disabled={loading}
                InputLabelProps={{
                  sx: { color: '#2e7d32' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#2e7d32' },
                    '&:hover fieldset': { borderColor: '#2e7d32' },
                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                  },
                }}
              />
            </motion.div>

            <motion.div variants={fieldVariants}>
              <TextField
                label="Slider Image URL"
                id="sliderImg"
                value={sliderImg}
                onChange={(e) => setSliderImg(e.target.value)}
                required
                fullWidth
                variant="outlined"
                disabled={loading}
                InputLabelProps={{
                  sx: { color: '#2e7d32' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#2e7d32' },
                    '&:hover fieldset': { borderColor: '#2e7d32' },
                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                  },
                }}
              />
            </motion.div>

            <motion.div variants={fieldVariants}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <FormControl
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#2e7d32' },
                      '&:hover fieldset': { borderColor: '#2e7d32' },
                      '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                    flex: 1,
                  }}
                >
                  <InputLabel id="clientName-label" sx={{ color: '#2e7d32' }}>
                    Select Brand
                  </InputLabel>
                  <Select
                    labelId="clientName-label"
                    id="clientName"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    label="Select Brand"
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>-- Select Brand --</em>
                    </MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand._id} value={brand.brand_name}>
                        {brand.brand_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="trending"
                      checked={trending}
                      onChange={(e) => setTrending(e.target.checked)}
                      disabled={loading}
                      sx={{
                        color: '#2e7d32',
                        '&.Mui-checked': {
                          color: '#2e7d32',
                        },
                      }}
                    />
                  }
                  label="Trending"
                />
              </Box>
            </motion.div>

            <motion.div variants={fieldVariants}>
              <FormControl fullWidth variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#2e7d32' },
                    '&:hover fieldset': { borderColor: '#2e7d32' },
                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                  },
                }}
              >
                <InputLabel id="status-label" sx={{ color: '#2e7d32' }}>
                  Status
                </InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="Status"
                  disabled={loading}
                >
                  <MenuItem value="live">Live</MenuItem>
                  <MenuItem value="pause">Pause</MenuItem>
                </Select>
              </FormControl>
            </motion.div>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <motion.div variants={buttonVariants} whileHover="hover">
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                  sx={{ minWidth: 100, color: '#2e7d32', borderColor: '#2e7d32' }}
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div variants={buttonVariants} whileHover="hover">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ minWidth: 100, backgroundColor: '#2e7d32' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Slider'}
                </Button>
              </motion.div>
            </Box>
          </Box>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </motion.div>
    </Modal>
  );
};

export default SliderEdit;